import React, { useEffect, useState } from "react";
import "./App.css";
import router from "./route";
import {
  Box,
  createTheme,
  ThemeProvider,
  Typography,   
  CssBaseline,
} from "@mui/material";
import { RouterProvider } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { bannerData, fetchEnv, appVersion } from "./reducers/resourceReducer";
import RouteChangeTracker from "./components/RouteChangeTracker";
import { analytics, errorAnalytics } from "./helper/helper";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { isWeb } from "./utils/platform";
import { Capacitor } from "@capacitor/core";
import { channel, ENVIRONMENT } from "./reducers/userReducer";
import useAuthStatus from "./helper/useAuthStatus";
import { getUTMParams } from "./helper/helper";
import { LiveUpdate } from "@capawesome/capacitor-live-update";
import { getDesignTokens } from "./constants/theme";
import clevertapService from "./services/clevertap";

const Auth = () => {
  const dispatch = useDispatch();
  const { loading, error } = useAuthStatus();
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [mode, setMode] = useState("light");
  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === "light" ? "light" : "light"));
  };

  useEffect(() => {
    const utmParams = getUTMParams();
    if (Object.keys(utmParams).length > 0) {
      localStorage.setItem("utmParams", JSON.stringify(utmParams));
    }
    // Load saved theme preference from localStorage
    const savedTheme = localStorage.getItem("theme-mode");
    if (savedTheme) {
      setMode(savedTheme);
    } else if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      // Use system preference as fallback
      setMode("light");
    }

    // Add event listener for system theme changes
    const mediaQuery =
      window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = (e) => {
      if (!localStorage.getItem("theme-mode")) {
        setMode(e.matches ? "light" : "light");
      }
    };

    if (mediaQuery && typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleSystemThemeChange);
    }
    window.addEventListener("toggle-theme", toggleColorMode);

    return () => {
      if (mediaQuery && typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", handleSystemThemeChange);
      }
      window.removeEventListener("toggle-theme", toggleColorMode);
    };
  }, []);

  // Add a function to expose theme toggle to other components
  useEffect(() => {
    window.toggleTheme = toggleColorMode;
  }, []);

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem("theme-mode", mode);
  }, [mode]);

  useEffect(() => {
    
    if (!isWeb()) {
      // App.getInfo()
      //   .then((info) => {
      //     setVersion(info.version);
      //   })
      //   .catch((e) => {
      //     console.log(e);
      //   });
    }
    
    const initializeApp = async () => {
      try {
        try {
          await clevertapService.initialize();
          await LiveUpdate.ready();
          const currentBundle = await LiveUpdate.getCurrentBundle();
          const profile = localStorage.getItem("profile");
          const result = await LiveUpdate.sync({
            channel: "prod",
            customId: JSON.parse(profile || "{}")?.user_id,
          });
          
          if (result.nextBundleId) {
            LiveUpdate.reload();
            analytics.track("Live Update", {
              result: result.nextBundleId,
            });
          }
          const channel = await LiveUpdate.getChannel();
          console.log(channel, "channel");
        } catch (error) {
          errorAnalytics("Error checking for live updates", {
            error: error.message,
          });
        }
        
        // Initialize CleverTap
        
        dispatch(fetchEnv());
        dispatch(bannerData());
        if (isWeb()) {
          await ScreenOrientation.lock({ orientation: "portrait" });
        }
      } catch (error) {
        errorAnalytics("Error initializing app", { error: error.message });
      }
    };

    initializeApp();

    // Cleanup function to unlock orientation when component unmounts
    return () => {
      const unlockOrientation = async () => {
        try {
          await ScreenOrientation.unlock();
        } catch (error) {
          console.error("Error unlocking screen orientation:", error);
        }
      };
      unlockOrientation();
    };
  }, [dispatch]);

  const handleUpdate = async () => {
    try {
      await LiveUpdate.reload();
      analytics.track("Live Update Applied", {
        action: "reload",
      });
    } catch (error) {
      errorAnalytics("Error applying live update", {
        error: error.message,
      });
    }
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    maxWidth: "90%",
    bgcolor: theme.palette.brand.background,
    borderRadius: 2,
    boxShadow: 24,
    p: 2,
  };

  return (
    <ThemeProvider theme={theme}>
      {ENVIRONMENT() && (
        <Box
          position={"fixed"}
          top={"0%"}
          left={"50%"}
          zIndex={99999999}
          sx={{
            backgroundColor: "custom.error",
            boxShadow: 1,
            color: "text.invert",
            textAlign: "center",
            transform: "translate(-50%, 0%)",
          }}
        >
          {ENVIRONMENT()}
        </Box>
      )}
      <CssBaseline />
      <Modal open={showUpdateModal} aria-labelledby="update-app">
        <Box
          sx={style}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Typography
            id="update-app"
            variant="h6"
            fontWeight={600}
            textAlign="center"
          >
            A new version is available
          </Typography>
          <Typography variant="body2" textAlign="center" sx={{ mt: 1, mb: 2 }}>
            Please update the app to get the latest features and improvements
          </Typography>
          <Button
            variant="contained"
            size="large"
            color="brand"
            sx={{ mt: 2 }}
            onClick={handleUpdate}
          >
            <Typography color={"white"}>UPDATE NOW</Typography>
          </Button>
        </Box>
      </Modal>
      <Modal open={isUpdateAvailable} aria-labelledby="force-update-app">
        <Box
          sx={style}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Typography
            id="force-update-app"
            variant="h6"
            fontWeight={600}
            textAlign="center"
          >
            Please update the app to continue
          </Typography>
          <Button
            variant="contained"
            size="large"
            color="brand"
            sx={{ mt: 2 }}
            onClick={() => {
              switch (Capacitor.getPlatform()) {
                case "ios":
                  window.open(
                    "https://apps.apple.com/us/app/crobo/id6474628320",
                    "_blank"
                  );
                  analytics.track("App Store Button Clicked", {
                    category: "Update App",
                    label: "App Store Link",
                    url: "https://apps.apple.com/us/app/crobo/id6474628320",
                  });
                  break;
                case "android":
                  window.open(
                    "https://play.google.com/store/apps/details?id=io.ionic.CROBO",
                    "_blank"
                  );
                  analytics.track("Google Play Button Clicked", {
                    category: "Update App",
                    label: "Google Play Link",
                    url: "https://play.google.com/store/apps/details?id=io.ionic.CROBO",
                  });
                  break;
                default:
                  break;
              }
            }}
          >
            <Typography color={"white"}>UPDATE</Typography>
          </Button>
        </Box>
      </Modal>
      <RouterProvider
        router={router}
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <RouteChangeTracker />
      </RouterProvider>
    </ThemeProvider>
  );
};

export default Auth;
